import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { NewsCard, type NewsCardStat } from "@navi-soft/ui/news-card";
import {
  useInfiniteScrollSentinel,
  useOffsetInfiniteList,
} from "@navi-soft/ui/use-offset-infinite-list";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import { fetchDrops, fetchMe, type DropItem } from "@/lib/api";
import { useFormatInstantDisplay } from "@/lib/format-datetime";

export const Route = createFileRoute("/")({
  component: NewsPage,
});

function NewsPage() {
  const t = useTranslations("app");
  const formatInstant = useFormatInstantDisplay();
  const scrollRef = useRef<HTMLElement>(null);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await fetchMe();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
  const authenticated = Boolean(meQuery.data?.authenticated);

  const fetchPage = useCallback(async ({ limit, offset }: { limit: number; offset: number }) => {
    const res = await fetchDrops(limit, offset);
    if (!res.ok) throw new Error(res.error);
    return { list: res.data.items, total_count: res.data.total_count };
  }, []);

  const {
    items,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
  } = useOffsetInfiniteList<DropItem>({
    fetcher: fetchPage,
    resetKey: "news",
    enabled: authenticated,
    getId: (d) => d.drop_id,
    queryKey: ["consortium", "drops"],
  });

  const sentinelRef = useInfiniteScrollSentinel(loadMore, {
    rootRef: scrollRef,
    enabled: authenticated && hasMore && !loading,
  });

  return (
    <ForumPageShell scrollRef={scrollRef}>
      <ForumPageChrome
        eyebrow={t("newsEyebrow")}
        title={t("navNews")}
        intro={t("newsIntro")}
      />

      {!authenticated ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("newsNeedLogin")}
        </p>
      ) : loading ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("loading")}</p>
      ) : error ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("dropsError")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("dropsEmpty")}
        </p>
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-4">
            {items.map((drop) => (
              <li key={drop.drop_id}>
                <DropNewsCard
                  drop={drop}
                  t={t}
                  when={
                    drop.published_at
                      ? formatInstant(drop.published_at)
                      : "—"
                  }
                />
              </li>
            ))}
          </ul>
          <div ref={sentinelRef} className="h-8 w-full" aria-hidden>
            {loadingMore ? (
              <p className="py-2 text-center text-ui-micro text-[var(--brand-text-muted)]">
                {t("newsLoadingMore")}
              </p>
            ) : null}
          </div>
        </>
      )}
    </ForumPageShell>
  );
}

function DropNewsCard({
  drop,
  when,
  t,
}: {
  drop: DropItem;
  when: string;
  t: ReturnType<typeof useTranslations<"app">>;
}) {
  const yard = drop.system_name?.trim();
  const presenceOnly = isYardPresenceOnly(drop.item_counts);
  const stats = presenceOnly ? [] : toStats(drop.item_counts, t);
  const title = yard
    ? t("newsDropTitleYard", { yard })
    : t("newsDropTitleUnknown");
  const description = presenceOnly
    ? t("newsYardJoined")
    : stats.length > 0
      ? t("newsDropDescription", {
          summary: stats.map((s) => `${s.label}: ${s.value}`).join(" · "),
        })
      : t("newsDropDescriptionEmpty");

  return (
    <NewsCard
      eyebrow={t("newsDropEyebrow")}
      title={title}
      description={description}
      stats={stats}
      meta={when}
      action={t("newsDropAction")}
    />
  );
}

function isYardPresenceOnly(counts: Record<string, unknown>): boolean {
  const keys = Object.entries(counts)
    .filter(([, raw]) => {
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(n) && n > 0;
    })
    .map(([key]) => key);
  return (
    keys.length === 1 &&
    (keys[0] === "yard_network_presence" || keys[0] === "yard")
  );
}

function toStats(
  counts: Record<string, unknown>,
  t: ReturnType<typeof useTranslations<"app">>,
): NewsCardStat[] {
  const order = [
    "part_catalog",
    "part",
    "platform",
    "hull_snapshot",
    "supersedes",
    "bulletin",
    "care_campaign",
    "yard_network_presence",
    "yard",
  ] as const;

  const labelFor = (key: string): string | null => {
    switch (key) {
      case "part_catalog":
      case "part":
        return t("newsStatParts");
      case "platform":
        return t("newsStatPlatforms");
      case "hull_snapshot":
        return t("newsStatHulls");
      case "supersedes":
        return t("newsStatSupersedes");
      case "bulletin":
        return t("newsStatBulletins");
      case "care_campaign":
        return t("newsStatCare");
      case "yard_network_presence":
      case "yard":
        return t("newsStatYard");
      default:
        return null;
    }
  };

  const seen = new Set<string>();
  const out: NewsCardStat[] = [];

  for (const key of order) {
    const raw = counts[key];
    if (raw == null) continue;
    const label = labelFor(key);
    if (!label || seen.has(label)) continue;
    seen.add(label);
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) continue;
    out.push({ label, value: n });
  }

  for (const [key, raw] of Object.entries(counts)) {
    if ((order as readonly string[]).includes(key)) continue;
    const label = labelFor(key) ?? key.replaceAll("_", " ");
    if (seen.has(label)) continue;
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) continue;
    seen.add(label);
    out.push({ label, value: n });
  }

  return out;
}
